import React, { useState, useRef, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Book/Book.css";
import swapIcon from "../Book/swap2.png";
import axios from "axios";
import { API_URL, API_KEY } from "../../axiosConfig";

const Book = ({ onSearch }) => {
  const [tripType, setTripType] = useState("round");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromAirportCode, setFromAirportCode] = useState("");
  const [toAirportCode, setToAirportCode] = useState("");
  const [departDate, setDepartDate] = useState(new Date());
  const [returnDate, setReturnDate] = useState(null);
  const [passengerCount, setPassengerCount] = useState(1);
  const [travelClass, setTravelClass] = useState("Economy");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [departSuggestions, setDepartSuggestions] = useState([]);
  const [arrivalSuggestions, setArrivalSuggestions] = useState([]);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [allAirports, setAllAirports] = useState([]);
  const [isLoadingParams, setIsLoadingParams] = useState(true);
  const popupRef = useRef(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const parseDateWithTimezone = (dateString) => {
    if (!dateString) return null;
    
    const dateParts = dateString.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1;
    const day = parseInt(dateParts[2]);
    
    const date = new Date(year, month, day);
    date.setHours(12, 0, 0, 0);
    
    return date;
  };

  useEffect(() => {
    const loadAndApplySearchParams = () => {
      try {
        const savedParams = sessionStorage.getItem('flightSearchParams');
        if (!savedParams) {
          console.log("No search parameters found in session storage");
          return;
        }
        
        console.log("Loading search parameters from session:", savedParams);
        const params = JSON.parse(savedParams);
        
        if (params.tripType) {
          setTripType(params.tripType === "one-way" ? "one-way" : "round");
        }
        
        if (params.originName) {
          setFrom(params.originName);
        }
        
        if (params.origin) {
          setFromAirportCode(params.origin);
        }
        
        if (params.destinationName) {
          setTo(params.destinationName);
        }
        
        if (params.destination) {
          setToAirportCode(params.destination);
        }
        
        if (params.departureDate) {
          try {
            const parts = params.departureDate.split('-');
            const date = new Date(
              parseInt(parts[0]), 
              parseInt(parts[1]) - 1, 
              parseInt(parts[2]), 
              12, 0, 0
            );
            console.log("Setting departure date:", date);
            setDepartDate(date);
          } catch (err) {
            console.error("Error parsing departure date:", params.departureDate, err);
          }
        }
        
        if (params.returnDate) {
          try {
            const parts = params.returnDate.split('-');
            const date = new Date(
              parseInt(parts[0]), 
              parseInt(parts[1]) - 1, 
              parseInt(parts[2]), 
              12, 0, 0
            );
            console.log("Setting return date:", date);
            setReturnDate(date);
          } catch (err) {
            console.error("Error parsing return date:", params.returnDate, err);
          }
        }
        
        if (params.travelers) {
          setPassengerCount(parseInt(params.travelers));
        }
        
        if (params.travelClass) {
          setTravelClass(params.travelClass);
        }
        
        console.log("Successfully applied search parameters to UI");
      } catch (error) {
        console.error("Error applying search parameters:", error);
      }
    };

    loadAndApplySearchParams();
    const delayedLoad = setTimeout(() => {
      loadAndApplySearchParams();
    }, 500);
    
    return () => clearTimeout(delayedLoad);
  }, []);

  useEffect(() => {
    const fetchAllAirports = async () => {
      try {
        const response = await axios.get(`${API_URL}`, {
          headers: { 
            'Content-Type': 'application/json',
            'X-API-KEY': 'XgJlPqMbKzVnWyDrFtChSuAe',
            'Accept': 'text/plain'
          }
        });
        
        console.log("Airports API Response:", response.data);
        
        if (response.data && Array.isArray(response.data)) {
          setAllAirports(response.data);
          
          const savedParams = sessionStorage.getItem('flightSearchParams');
          if (savedParams) {
            loadSearchParams(response.data, savedParams);
          } else {
            console.log("No saved search parameters found");
            setIsLoadingParams(false);
          }
        } else {
          console.error("Unexpected API format:", response.data);
          setError("Invalid response format from API.");
          setIsLoadingParams(false);
        }
      } catch (err) {
        console.error("API Fetch Error:", err);
        setError(`Server error: ${err.message || 'Unknown error'}`);
        setIsLoadingParams(false);
      }
    };
  
    fetchAllAirports();
  }, []);



  const loadSearchParams = (airportsData, savedParamsString) => {
    try {
      const params = JSON.parse(savedParamsString);
      console.log("Successfully parsed search params:", params);
      
      if (params.tripType) {
        setTripType(params.tripType === "one-way" ? "one-way" : "round");
      }
      
      if (params.departureDate) {
        const departureDate = parseDateString(params.departureDate);
        setDepartDate(departureDate);
      }
      
      if (params.returnDate) {
        const returnDate = parseDateString(params.returnDate);
        setReturnDate(returnDate);
      }
      
      if (params.origin) {
        setFromAirportCode(params.origin);
      }
      
      if (params.destination) {
        setToAirportCode(params.destination);
      }
      
      if (params.originName) {
        setFrom(params.originName);
      } else if (params.origin) {
        const fromAirport = airportsData.find(airport => 
          airport.airportCode === params.origin
        );
        setFrom(fromAirport ? fromAirport.airportName : params.origin);
      }
      
      if (params.destinationName) {
        setTo(params.destinationName);
      } else if (params.destination) {
        const toAirport = airportsData.find(airport => 
          airport.airportCode === params.destination
        );
        setTo(toAirport ? toAirport.airportName : params.destination);
      }
      
      if (params.travelers) {
        setPassengerCount(parseInt(params.travelers));
      }
      
      if (params.travelClass) {
        setTravelClass(params.travelClass);
      }
      
    } catch (error) {
      console.error("Error parsing saved search parameters:", error);
    } finally {
      setIsLoadingParams(false);
    }
  };

  const calculateRelevanceScore = (airport, query) => {
    const airportName = airport.airportName.toLowerCase();
    const airportCode = (airport.airportCode || "").toLowerCase();
    const searchTerm = query.toLowerCase();
    
    if (airportCode === searchTerm) return 100;
    if (airportCode.startsWith(searchTerm)) return 90;
    if (airportName.startsWith(searchTerm)) return 80;
    if (airportName.includes(` ${searchTerm}`)) return 70;
    if (airportName.includes(searchTerm)) return 60;
    if (airportCode.includes(searchTerm)) return 50;
    
    return 0;
  };

  const fetchLocations = useCallback(async (query, setSuggestions) => {
    if (query.length < 1) {
      return setSuggestions([]);
    }

    const filteredAirports = allAirports
      .filter((airport) =>
        airport.airportName.toLowerCase().includes(query.toLowerCase()) ||
        (airport.airportCode && airport.airportCode.toLowerCase().includes(query.toLowerCase()))
      )
      .sort((a, b) => {
        const scoreA = calculateRelevanceScore(a, query);
        const scoreB = calculateRelevanceScore(b, query);
        return scoreB - scoreA;
      })
      .slice(0, 7);
      
    setSuggestions(filteredAirports);
  }, [allAirports]);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFetchLocations = useCallback(debounce(fetchLocations, 300), [fetchLocations]);

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFrom(value);
    setFromAirportCode("");
    debouncedFetchLocations(value, setDepartSuggestions);
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    setToAirportCode("");
    debouncedFetchLocations(value, setArrivalSuggestions);
  };

  const handleAirportSelect = (airport, type) => {
    if (type === "departure") {
      setFrom(airport.airportName);
      setFromAirportCode(airport.airportCode);
      setDepartSuggestions([]);
    } else {
      setTo(airport.airportName);
      setToAirportCode(airport.airportCode);
      setArrivalSuggestions([]);
    }
  };

  const parseDateString = (dateString) => {
    if (!dateString) return null;
    
    try {
      const dateParts = dateString.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[2]);
      
      // Create date in local timezone without time components
      const date = new Date(year, month, day);
      
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date created");
      }
      
      return date;
    } catch (error) {
      console.error("Error parsing date string:", dateString, error);
      return null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    console.log("Book component state updated:", {
      from,
      to,
      fromAirportCode,
      toAirportCode,
      departDate,
      returnDate,
      isLoadingParams
    });
  }, [from, to, fromAirportCode, toAirportCode, departDate, returnDate, isLoadingParams]);

  const swapLocations = () => {
    setFrom(to);
    setTo(from);
    setFromAirportCode(toAirportCode);
    setToAirportCode(fromAirportCode);
  };

  const handleSearch = () => {
    if (!fromAirportCode || !toAirportCode) {
      setError("Please select valid departure and arrival airports");
      return;
    }
  
    if (tripType === "round" && !returnDate) {
      setError("Please select a return date");
      return;
    }
  
    setError("");
  
    const formatDateForDisplay = (date) => {
      if (!date) return null;
      const safeDate = new Date(date);
      safeDate.setHours(12, 0, 0, 0);
      return safeDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };
  
    const departureDateFormatted = formatDateForDisplay(departDate);
    const returnDateFormatted = returnDate ? formatDateForDisplay(returnDate) : null;
  
    const formatDateForAPI = (date) => {
      if (!date) return null;
      const d = new Date(date);
      // Use local date components to avoid timezone issues
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    const searchData = {
      origin: fromAirportCode,
      originName: from,
      destination: toAirportCode,
      destinationName: to,
      departureDate: formatDateForAPI(departDate),
      returnDate: tripType === "round" ? formatDateForAPI(returnDate) : null,
      travelers: passengerCount,
      tripType: tripType === "round" ? "round-trip" : "one-way",
      travelClass: travelClass,
      fromFullName: `${from} (${fromAirportCode})`,
      toFullName: `${to} (${toAirportCode})`,
      classSelected: travelClass,
      departureDateFormatted,
      returnDateFormatted,
      searchTimestamp: new Date().toISOString()
    };
  
    console.log('Saving search data:', {
      ...searchData,
      rawDepartDate: departDate,
      rawReturnDate: returnDate
    });
  
    try {
      sessionStorage.setItem("flightSearchParams", JSON.stringify(searchData));
    } catch (error) {
      console.error('Failed to save search params:', error);
    }
  
    if (typeof onSearch === "function") {
      onSearch(searchData);
    } else {
      console.warn("onSearch prop not provided to Book component");
    }
  };

  
  // Removed the auto-search useEffect hook

  const datePickerStyles = isMobile ? {
    popperClassName: "mobile-date-picker-popper",
    popperPlacement: "auto",
    popperModifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 5],
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 20,
        },
      },
    ],
    calendarClassName: "mobile-calendar",
  } : {};

  const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <div 
      onClick={onClick}
      className="custom-datepicker-input"
      ref={ref}
    >
      {value || placeholder || "Select Date"}
    </div>
  ));

  return (
    <div className="book-container">
     <div className="booking-form">
     <div className="trip-type-row">
      <div className="trip-type-b">
        <label key="one-way-label">
          <input
            type="radio"
            value="one-way"
            checked={tripType === "one-way"}
            onChange={() => setTripType("one-way")}
          />{" "}
          One Way
        </label>
        <label key="round-trip-label">
          <input
            type="radio"
            value="round"
            checked={tripType === "round"}
            onChange={() => setTripType("round")}
          />{" "}
          Round Trip
        </label>
      </div>
      </div>
      </div>
      <div className="inputs-row">
      <div className="location-container">
        <div className="location">
          <label>From</label>
          <input
            type="text"
            value={from}
            onChange={handleFromChange}
            placeholder="Enter city or airport"
          />
          {departSuggestions.length > 0 && (
            <ul className="absolute-dropdown bg-dark border rounded rp shadow-md max-h-48 overflow-y-auto z-10">
              {departSuggestions.map((airport) => (
                <li
                  key={airport.id}
                  onClick={() => handleAirportSelect(airport, "departure")}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                >
                  {airport.airportName} ({airport.airportCode})
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="swap-icon" onClick={swapLocations}>
          <img src={swapIcon} alt="Swap Locations" />
        </div>

        <div className="location">
          <label>To</label>
          <input
            type="text"
            value={to}
            onChange={handleToChange}
            placeholder="Enter city or airport"
          />
          {arrivalSuggestions.length > 0 && (
            <ul className="absolute-dropdown bg-dark border rounded rp shadow-md max-h-48 overflow-y-auto z-10">
              {arrivalSuggestions.map((airport) => (
                <li
                  key={airport.id}
                  onClick={() => handleAirportSelect(airport, "arrival")}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                >
                  {airport.airportName} ({airport.airportCode})
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="d-r">
      <div className={`d-r d-rr ${tripType === "one-way" ? "trip-type-one-way" : ""}`}>
      <div className="date-picker-container">
  <label>Depart</label>
  <DatePicker
    selected={departDate}
    onChange={(date) => {
      // Create new date at noon local time
      const adjustedDate = new Date(date);
      adjustedDate.setHours(12, 0, 0, 0);
      setDepartDate(adjustedDate);
      if (tripType === "round" && returnDate && returnDate < adjustedDate) {
        setReturnDate(null);
      }
    }}
    dateFormat="dd/MM/yyyy"
    minDate={new Date(2018, 0, 1)}
    {...datePickerStyles}
    portalId={isMobile ? "date-picker-portal" : undefined}
    withPortal={isMobile}
    customInput={isMobile ? <CustomInput /> : null}
  />
  <span className="date-picker-iconn">📅</span>
</div>
       {tripType === "round" && (
  <div className="date-picker-container">
    <label>Return</label>
    <DatePicker
      selected={returnDate}
      onChange={(date) => {
        // Create new date at noon local time
        const adjustedDate = new Date(date);
        adjustedDate.setHours(12, 0, 0, 0);
        setReturnDate(adjustedDate);
      }}
      dateFormat="dd/MM/yyyy"
      minDate={departDate}
      disabled={!departDate}
      {...datePickerStyles}
      portalId={isMobile ? "date-picker-portal" : undefined}
      withPortal={isMobile}
      customInput={isMobile ? <CustomInput /> : null}
    />
    <span className="date-picker-iconn">📅</span>
  </div>
)}
      </div>

      <div className="passenger-class-container">
    
  <div className="passenger-dropdown">
    <label>Passengers</label>
    <select 
      value={passengerCount} 
      onChange={(e) => setPassengerCount(Number(e.target.value))}
    >
      {[...Array(4).keys()].map((num) => (
        <option key={`passenger-${num + 1}`} value={num + 1}>
          {num + 1} {num === 0 ? 'Passenger' : 'Passengers'}
        </option>
      ))}
    </select>
  </div>
 

  <div className="class-dropdown">
    <label>Class</label>
    <select 
      value={travelClass} 
      onChange={(e) => setTravelClass(e.target.value)}
    >
      <option value="Economy">Economy</option>
      <option value="Premium Economy">Premium Economy</option>
      <option value="Business">Business</option>
      <option value="First">First</option>
    </select>
  </div>
  
</div>
      <button className="search-btn" onClick={handleSearch}>Search</button>
     </div>
      
      <div id="date-picker-portal" />
      
      <style>{`
        .custom-datepicker-input {
    background: white;
    border: 1px solid #ccc;
    border-radius: 7px;
    padding: 10px 12px;
    cursor: pointer;
    width: 100%;
    height: 100%;
    display: flex
;
    align-items: center;
}
      `}</style>
    </div>
  );
};

Book.defaultProps = {
  onSearch: null
};

export default Book;